import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ColDef,
  ExpandOrCollapseAllEvent,
  GridApi,
  GridOptions,
  RowGroupOpenedEvent,
  ValueSetterParams,
} from 'ag-grid-community';
import { GroupingColumn, Row, UseControlledAGGridProps } from '../types';
import { isGhostRow } from '../types';
import { getRowDragText } from '../utils';
import {
  objectColumnDef,
  PartialInitialColumnState,
  primitiveColumnDef,
  sharedColumnDef,
  withoutDefaultFlex,
} from '../columnDefs';
import { GroupCellEditor } from '../GroupCellEditor';
import { GroupCellRenderer } from '../GroupCellRenderer';

export const useGrouping = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi<Row<RowData, RowId>> | null,
  partialInitialColumnState: PartialInitialColumnState<RowData>,
  groupingColumn: GroupingColumn<RowData, RowId> | undefined
) => {
  const groupingEnabled = groupingColumn !== undefined;
  const groupingRef = useRef(groupingColumn);
  useEffect(() => {
    groupingRef.current = groupingColumn;
  }, [groupingColumn]);

  // Stable callback handed to cell renderers via context. It delegates to the
  // live groupingRef so the chevron always calls the current onRowExpansionToggle
  // without capturing a stale closure in AG Grid's cached cellRendererParams.
  const handleRowExpansionToggle = useCallback(
    (rowId: string, expanded: boolean) =>
      groupingRef.current?.onRowExpansionToggle(rowId as RowId, expanded),
    []
  );
  const reorderableEnabled = props.reorderable !== undefined;
  const dragAndDroppableEnabled = props.dragAndDroppable !== undefined;
  const cellRendererDefined = groupingEnabled
    ? 'cellRenderer' in groupingColumn
      ? groupingColumn.cellRenderer !== undefined
      : false
    : false;

  const InnerCellRenderer = groupingEnabled
    ? 'InnerCellRenderer' in groupingColumn
      ? groupingColumn.InnerCellRenderer
      : undefined
    : undefined;

  // Either from reorderable or dragAndDroppable props
  const rowDragTextFn =
    props.reorderable?.rowDragText ?? props.dragAndDroppable?.rowDragText;

  const showDragHandle = reorderableEnabled || dragAndDroppableEnabled;

  const autoGroupColumnDef: ColDef | undefined = useMemo(() => {
    if (!groupingEnabled) {
      return undefined;
    }

    const { cellRenderer, ...objectColumnDefs } =
      objectColumnDef(groupingColumn);

    const onCellValueChange =
      'onCellValueChange' in groupingColumn
        ? groupingColumn.onCellValueChange
        : null;

    // Create a valueSetter that reads from groupingRef at invocation time
    // This avoids stale closures when AG Grid caches column definitions
    const groupingValueSetter = onCellValueChange
      ? (params: ValueSetterParams<Row<RowData, RowId>>) => {
          const currentOnCellValueChange =
            groupingRef.current && 'onCellValueChange' in groupingRef.current
              ? groupingRef.current.onCellValueChange
              : null;
          if (currentOnCellValueChange) {
            currentOnCellValueChange({
              row: params.data,
              value: params.newValue,
            });
          }
          // Since the change will come back through the props, we tell AG Grid that
          // nothing has changed yet.
          return false;
        }
      : undefined;

    const editableProps = onCellValueChange
      ? {
          editable: true,
          cellEditor: GroupCellEditor,
          cellEditorParams: {
            showDragHandle,
            labels: props.groupCellLabels,
          },
          valueSetter: groupingValueSetter,
        }
      : {};

    const groupColumnDef: ColDef = {
      ...primitiveColumnDef(groupingColumn),
      ...sharedColumnDef(groupingColumn, 0, {
        ...partialInitialColumnState,
        groupingEnabled,
      }),
      ...objectColumnDefs,

      rowDrag: reorderableEnabled || dragAndDroppableEnabled,
      rowDragText: rowDragTextFn
        ? params => getRowDragText(params, api, rowDragTextFn)
        : undefined,

      ...(cellRendererDefined
        ? { cellRenderer }
        : {
            minWidth: 250,
            cellRenderer: GroupCellRenderer,
            cellRendererParams: {
              InnerCellRenderer,
              showDragHandle,
              columnId: groupingColumn.id,
              labels: props.groupCellLabels,
            },
          }),

      ...editableProps,
    };

    return props.autoSizeStrategy
      ? withoutDefaultFlex(groupColumnDef, groupingColumn)
      : groupColumnDef;
  }, [
    groupingEnabled,
    groupingColumn,
    partialInitialColumnState,
    reorderableEnabled,
    dragAndDroppableEnabled,
    rowDragTextFn,
    InnerCellRenderer,
    showDragHandle,
    cellRendererDefined,
    api,
    props.groupCellLabels,
    props.autoSizeStrategy,
  ]);

  useEffect(() => {
    if (!api || !groupingEnabled) {
      return;
    }

    api.forEachNode(node => {
      if (node.group && node.id) {
        const shouldExpand = (groupingColumn.expandedIds as string[]).includes(
          node.id
        );
        api.setRowNodeExpanded(node, shouldExpand);
      }
    });
  }, [api, groupingColumn, groupingEnabled]);

  // Update context and refresh cells when expandedIds changes
  const expandedIds = groupingColumn?.expandedIds;
  useEffect(() => {
    if (!api || !groupingEnabled || !expandedIds) {
      return;
    }
    // Update context so cell renderers can access fresh expandedIds
    api.setGridOption('context', {
      expandedIds,
      onRowExpansionToggle: handleRowExpansionToggle,
    });
    api.refreshCells({
      columns: ['ag-Grid-AutoColumn'],
      force: true,
    });
  }, [api, groupingEnabled, expandedIds, handleRowExpansionToggle]);

  if (!groupingEnabled) {
    return { groupingEnabled, groupingProps: undefined };
  }

  const groupingProps: GridOptions<Row<RowData, RowId>> = {
    // Set initial context so cell renderers have expandedIds on first render
    context: { expandedIds, onRowExpansionToggle: handleRowExpansionToggle },
    isGroupOpenByDefault: params => {
      const currentGrouping = groupingRef.current;
      return currentGrouping
        ? (currentGrouping.expandedIds as string[]).includes(params.key)
        : false;
    },
    onRowGroupOpened: (event: RowGroupOpenedEvent<Row<RowData, RowId>>) => {
      const data = event.node.data;
      if (!data) {
        return;
      }

      const currentGrouping = groupingRef.current;
      if (currentGrouping) {
        currentGrouping.onRowExpansionToggle(data.id, event.node.expanded);
      }
    },
    onExpandOrCollapseAll: (
      event: ExpandOrCollapseAllEvent<Row<RowData, RowId>>
    ) => {
      const currentGrouping = groupingRef.current;
      if (!currentGrouping?.onExpandedIdsChange) {
        return;
      }

      const expandedIds: RowId[] = [];
      event.api.forEachNode(node => {
        if (node.group && node.expanded && node.id) {
          expandedIds.push(node.id as RowId);
        }
      });
      currentGrouping.onExpandedIdsChange(expandedIds);
    },
    treeData: true,
    autoGroupColumnDef,

    getDataPath: (data: Row<RowData, RowId>) => {
      // Ghost rows have no real data — place them at the top level so
      // AG Grid can create a valid row node that the external filter hides.
      if (isGhostRow(data)) {
        return [data.id];
      }

      if (!data.data) {
        return [];
      }

      if (!('path' in data)) {
        return [data.id];
      }

      return data.path ?? [];
    },
  };

  return { groupingEnabled, groupingProps };
};
